import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  TextField,
  CircularProgress,
  Box
} from '@mui/material';
import { adminApi } from 'src/utils/adminApi';
import PlayerInsightsSummary from './PlayerInsightsSummary';

export default function PlayerDetailDialog({ open, onClose, player, mode, onCompleted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [insightsPayload, setInsightsPayload] = useState(null);
  const [workloadPayload, setWorkloadPayload] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  useEffect(() => {
    if (!open || !player?.id) {
      return;
    }

    setError('');
    setInsightsPayload(null);
    setWorkloadPayload(null);
    setPassword('');
    setPasswordConfirmation('');

    if (mode === 'summary') {
      setLoading(true);
      Promise.all([adminApi.userInsights(player.id), adminApi.userWorkload(player.id)])
        .then(([insightsResult, workloadResult]) => {
          setInsightsPayload(insightsResult.data || null);
          setWorkloadPayload(workloadResult.data?.workload ?? null);
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }

  }, [open, player, mode]);

  const titles = {
    summary: 'Player insights',
    lock: player?.isBlocked ? 'Unlock player' : 'Lock player',
    delete: 'Delete player',
    password: 'Change password'
  };

  async function handleConfirm() {
    setError('');
    setLoading(true);

    try {
      if (mode === 'lock') {
        await adminApi.blockUser(player.id, !player.isBlocked);
      } else if (mode === 'delete') {
        await adminApi.deleteUser(player.id);
      } else if (mode === 'password') {
        await adminApi.changePassword(player.id, password, passwordConfirmation);
      }

      onCompleted?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: '#F3F4F6' }}>{titles[mode] || 'Player'}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: mode === 'summary' ? '#0B0E14' : undefined }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && mode === 'summary' && insightsPayload && (
          <PlayerInsightsSummary
            insights={insightsPayload}
            workload={workloadPayload}
            playerName={player?.name}
          />
        )}

        {mode === 'password' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="password"
              label="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
            />
          </Stack>
        )}

        {mode === 'delete' && (
          <Typography sx={{ color: '#9CA3AF' }}>
            Delete {player?.name}? This cannot be undone.
          </Typography>
        )}

        {mode === 'lock' && (
          <Typography sx={{ color: '#9CA3AF' }}>
            {player?.isBlocked
              ? `Unlock ${player?.name} and restore app access.`
              : `Lock ${player?.name} and revoke mobile access.`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {['lock', 'delete', 'password'].includes(mode) && (
          <Button variant="contained" onClick={handleConfirm} disabled={loading}>
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
